import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Route, Utensils, Globe2, Compass, MessageSquare, Radio, Coins, ArrowRight, Siren } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { countryOfDay } from "@/lib/daily";
import { CountryOfDay, ChallengeCard, PassportCard } from "@/components/DailyStrip";
import usePoints from "@/lib/usePoints";
import { INTRO } from "@/lib/introContent";

const TILE = "group relative overflow-hidden rounded-2xl p-4 flex flex-col justify-between min-h-[112px] text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]";

function Tile({ to, href, color, icon: Icon, label, sub, testid, span = "" }) {
  const inner = (
    <>
      <span className="absolute -end-4 -bottom-5 w-24 h-24 rounded-full bg-white/10 transition-transform duration-300 group-hover:scale-125" />
      <Icon className="w-7 h-7 relative" />
      <span className="relative">
        <span className="block font-display font-bold text-lg leading-tight">{label}</span>
        {sub && <span className="block text-[11px] text-white/80 mt-0.5">{sub}</span>}
      </span>
      <ArrowRight className="absolute top-4 end-4 w-4 h-4 text-white/60 rtl:rotate-180 group-hover:text-white transition-colors" />
    </>
  );
  const cls = `${TILE} ${span}`;
  return href ? <a href={href} className={cls} style={{ background: color }} data-testid={testid}>{inner}</a> : <Link to={to} className={cls} style={{ background: color }} data-testid={testid}>{inner}</Link>;
}

function PointsTile() {
  const { t } = useLang();
  const { balance, badges } = usePoints();
  return (
    <Link to="/passport" className={`${TILE} bg-ink col-span-2`} data-testid="points-tile">
      <span className="absolute -end-4 -bottom-5 w-24 h-24 rounded-full bg-ochre/20" />
      <Coins className="w-7 h-7 text-ochre relative" />
      <span className="relative">
        <span className="block font-display font-bold text-2xl leading-none stat-number" data-testid="points-balance">{balance}</span>
        <span className="block text-[11px] text-sand/80 mt-1" data-testid="points-badge">{badges.current ? t(`badge_${badges.current}`) : t("points_title")}{badges.next ? ` · ${t("badge_next").replace("{n}", badges.next.at - balance).replace("{b}", t(`badge_${badges.next.id}`))}` : ""}</span>
      </span>
    </Link>
  );
}

/** Home bento: country of the day + colourful entry tiles (people tap, they don't read). */
export default function HomeTiles({ countries, onSelect }) {
  const { t, lang } = useLang();
  const intro = INTRO[lang] || INTRO.en;
  return (
    <div className="mt-4 sm:mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="home-tiles">
      <CountryOfDay country={countryOfDay(countries)} onSelect={onSelect} />
      <Tile to="/introduzione" color="#C8553D" icon={Compass} label={t("nav_intro")} sub={intro.title} testid="tile-intro" />
      <Tile to="/itinerari" color="#3B6FA0" icon={Route} label={t("nav_itineraries")} sub={t("itineraries_sub")} testid="tile-itineraries" />
      <Tile to="/ricettario" color="#2F6F5E" icon={Utensils} label={t("nav_recipes")} sub={t("cookbook_title")} testid="tile-recipes" />
      <Tile to="/overview" color="#8C5A2B" icon={Globe2} label={t("nav_conclusion")} sub={t("overview_cta")} testid="tile-conclusion" />
      <ChallengeCard />
      <PassportCard total={countries.length} />
      <Tile to="/ebook" color="#B8860B" icon={BookOpen} label="E-book" sub={`${t("ebook_title")} · 0,99 €`} testid="tile-ebook" span="col-span-2" />
      <Tile href="#community" color="#5B4B8A" icon={MessageSquare} label={t("nav_community")} sub={t("reviews")} testid="tile-community" />
      <Tile to="/radio" color="#1F6F8B" icon={Radio} label={t("radio")} testid="tile-radio" />
      <Tile to="/strumenti" color="#B23A2E" icon={Siren} label={t("kit_title")} sub={t("kit_sub")} testid="tile-kit" span="col-span-2" />
      <PointsTile />
    </div>
  );
}
